import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API_URL =
  process.env.REACT_APP_CHATBOT_API_URL ||
  "https://chatbot-widget88.azurewebsites.net";
const DEFAULT_GREETING_TEXT = "Hello, how can I help you?";
const REQUEST_TIMEOUT_MS = 15000;
const CHAT_STORAGE_KEY = "chatbot-widget-conversation";
const CHAT_SESSION_KEY = "chatbot-widget-session-id";
const MAX_CONTEXT_MESSAGES = 8;

let warmupPromise;

const withTimeout = async (
  url,
  options = {},
  timeoutMs = REQUEST_TIMEOUT_MS,
) => {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timerId);
  }
};

const getChatErrorMessage = (error) => {
  const statusCode = error?.response?.status;
  const errorCode = error?.code;

  if (statusCode === 429) {
    return "I am receiving too many requests right now. Please try again in a moment.";
  }

  if (statusCode === 503 || statusCode === 504) {
    return "The chat service is temporarily unavailable. Please try again shortly.";
  }

  if (errorCode === "ECONNABORTED") {
    return "The request took too long and timed out. Please try again.";
  }

  return "Sorry, I am having trouble responding right now. Please try again.";
};

const warmUpBackend = () => {
  if (!warmupPromise) {
    warmupPromise = withTimeout(`${API_URL}/ping`).catch(() => null);
  }
  return warmupPromise;
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `chat-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const loadStoredConversation = (greetingText) => {
  // Always start with a default greeting message, no persistence.
  return [{ sender: "bot", text: greetingText, isGreeting: true }];
};

const buildConversationContext = (chatLog) =>
  chatLog
    .filter((entry) => entry?.sender && entry?.text)
    .slice(-MAX_CONTEXT_MESSAGES)
    .map((entry) => ({
      role: entry.sender === "user" ? "user" : "assistant",
      content: entry.text,
    }));

const sendMessage = async (
  message,
  userToken,
  timeoutMs = REQUEST_TIMEOUT_MS,
  conversationContext = [],
  sessionId,
) => {
  if (!userToken) {
    throw new Error("No token provided");
  }
  const response = await axios.post(
    `${API_URL}/chat`,
    {
      message,
      sessionId,
      conversationContext,
    },
    {
      timeout: timeoutMs,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    },
  );
  return response.data;
};

const fetchGreetingAudio = async (greetingText, userToken) => {
  if (!userToken) {
    throw new Error("No token provided");
  }

  const response = await axios.post(
    `${API_URL}/chat/greeting`,
    { message: greetingText },
    {
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    },
  );

  return response.data;
};

const ChatBot = ({
  userToken,
  greetingText = DEFAULT_GREETING_TEXT,
  autoPlayVoice = true,
}) => {
  const [sessionId] = useState(() => {
    if (typeof window === "undefined") {
      return createSessionId();
    }

    const storedSessionId = window.localStorage.getItem(CHAT_SESSION_KEY);
    if (storedSessionId) {
      return storedSessionId;
    }

    const nextSessionId = createSessionId();
    window.localStorage.setItem(CHAT_SESSION_KEY, nextSessionId);
    return nextSessionId;
  });
  const [message, setMessage] = useState("");
  const [chatLog, setChatLog] = useState(() =>
    loadStoredConversation(greetingText),
  );
  const [audioPath, setAudioPath] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWarmingUpMessage, setShowWarmingUpMessage] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [hasServerWarmupCompleted, setHasServerWarmupCompleted] =
    useState(false);
  const [pendingAutoplayAudioPath, setPendingAutoplayAudioPath] = useState("");
  const activeAudioRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatLog));
    } catch (error) {
      console.error("Error saving conversation:", error);
    }
  }, [chatLog]);

  useEffect(() => {
    let isMounted = true;

    warmUpBackend().finally(() => {
      if (isMounted) {
        setHasServerWarmupCompleted(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (activeAudioRef.current) {
        activeAudioRef.current.pause();
        activeAudioRef.current = null;
      }
    };
  }, []);

  const playAudioFromPath = (path, allowGestureRetry = false) => {
    if (!path) return;

    const normalizedAudioPath = path.startsWith("/") ? path.slice(1) : path;

    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current.currentTime = 0;
    }

    setAudioLoading(true);
    const audio = new Audio(`${API_URL}/${normalizedAudioPath}`);
    activeAudioRef.current = audio;

    audio.play().catch((error) => {
      if (activeAudioRef.current === audio) {
        setAudioLoading(false);
        activeAudioRef.current = null;
      }

      // Browser autoplay policies can block async playback until next user gesture.
      if (allowGestureRetry && error?.name === "NotAllowedError") {
        setPendingAutoplayAudioPath(path);
      }
    });

    audio.onended = () => {
      if (activeAudioRef.current === audio) {
        setAudioLoading(false);
        activeAudioRef.current = null;
      }
    };

    audio.onerror = () => {
      if (activeAudioRef.current === audio) {
        setAudioLoading(false);
        activeAudioRef.current = null;
      }
    };
  };

  useEffect(() => {
    if (!pendingAutoplayAudioPath) {
      return undefined;
    }

    const retryPlayback = () => {
      const pathToPlay = pendingAutoplayAudioPath;
      setPendingAutoplayAudioPath("");
      playAudioFromPath(pathToPlay, false);
    };

    document.addEventListener("pointerdown", retryPlayback, { once: true });
    document.addEventListener("keydown", retryPlayback, { once: true });

    return () => {
      document.removeEventListener("pointerdown", retryPlayback);
      document.removeEventListener("keydown", retryPlayback);
    };
  }, [pendingAutoplayAudioPath]);

  useEffect(() => {
    if (!autoPlayVoice || !greetingText || !userToken) {
      return;
    }

    let cancelled = false;

    const playGreeting = async () => {
      try {
        const data = await fetchGreetingAudio(greetingText, userToken);
        const greetingAudioPath = data?.audioPath || "";

        if (!cancelled && greetingAudioPath) {
          setAudioPath(greetingAudioPath);
          playAudioFromPath(greetingAudioPath, true);
        }
      } catch (error) {
        console.error("Error fetching greeting audio:", error);
      }
    };

    playGreeting();

    return () => {
      cancelled = true;
    };
  }, [autoPlayVoice, greetingText, userToken]);

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || loading) return;

    setMessage("");
    setChatLog((prevChatLog) => [
      ...prevChatLog,
      { sender: "user", text: trimmedMessage },
    ]);
    setLoading(true);

    const updatedChatLog = [
      ...chatLog,
      { sender: "user", text: trimmedMessage },
    ];

    if (!hasServerWarmupCompleted) {
      setShowWarmingUpMessage(true);
      await warmUpBackend();
      setHasServerWarmupCompleted(true);
      setShowWarmingUpMessage(false);
    }

    try {
      const response = await sendMessage(
        trimmedMessage,
        userToken,
        REQUEST_TIMEOUT_MS,
        buildConversationContext(updatedChatLog),
        sessionId,
      );
      const botReply = response?.choices?.[0]?.message?.content;

      setChatLog([
        ...updatedChatLog,
        {
          sender: "bot",
          text: botReply || "Sorry, I could not generate a response right now.",
        },
      ]);
      const responseAudioPath = response?.audioPath || "";
      setAudioPath(responseAudioPath);

      if (autoPlayVoice && responseAudioPath) {
        playAudioFromPath(responseAudioPath, true);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setChatLog((prevChatLog) => [
        ...prevChatLog,
        {
          sender: "bot",
          text: getChatErrorMessage(error),
        },
      ]);
      setAudioPath("");
    } finally {
      setLoading(false);
      setShowWarmingUpMessage(false);
    }
  };

  const handlePlayAudio = () => {
    playAudioFromPath(audioPath);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="p-3 sm:p-4 flex flex-col h-full" data-clarity-mask="true">
      <div className="flex-1 overflow-auto pr-1">
        {showWarmingUpMessage && (
          <div className="text-center mb-2">
            <span className="inline-block p-2 rounded bg-gray-200">
              Warming up.. loading data from server
            </span>
          </div>
        )}
        {chatLog.map((entry, index) => (
          <div
            key={index}
            className={`mb-2 flex ${
              entry.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <span
              className={`inline-block max-w-[84%] break-words text-sm leading-snug rounded-2xl px-3 py-1.5 ${
                entry.sender === "user"
                  ? "bg-blue-100 rounded-br-md"
                  : "bg-gray-100 rounded-bl-md"
              }`}
            >
              {entry.text}
            </span>
          </div>
        ))}
        {loading && (
          <div className="mb-2 flex justify-start">
            <span className="inline-block max-w-[84%] rounded-2xl rounded-bl-md px-3 py-1.5 bg-gray-100 animate-pulse text-sm leading-snug">
              ...
            </span>
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          className="flex-1 min-w-0 h-10 px-3 text-sm border rounded-md placeholder:text-xs sm:placeholder:text-sm"
          style={{ minWidth: 0 }}
          disabled={loading}
        />
        <button
          onClick={handleSendMessage}
          className="h-10 px-3 sm:px-4 text-sm text-white rounded-md whitespace-nowrap"
          style={{
            backgroundColor: loading ? "#CBC3E3" : "#5a00ff",
            flexShrink: 0,
          }}
          disabled={loading}
        >
          Send
        </button>
      </div>
      {audioPath && (
        <button
          onClick={handlePlayAudio}
          className="mt-4 px-4 py-2 text-white rounded flex items-center justify-center"
          style={{ backgroundColor: "#5a00ff", flexShrink: 0 }}
          disabled={audioLoading}
        >
          {audioLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            "Play Audio Response"
          )}
        </button>
      )}
    </div>
  );
};

export default ChatBot;
